import { Color } from '@persona-id/design-tokens';
import { omit, set, uniq } from 'lodash/fp';
import qs from 'qs';
import { generatePath, matchPath } from 'react-router-dom-v5-compat';

import {
  CONFIG_EDITOR_VERIFICATION_PATH,
  WORKFLOW_EDITOR_BASE_PATH,
  WORKFLOW_EDITOR_STEP_TOKEN_PATH,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import {
  FormFieldDirtyMap,
  ITEFormDataKey,
  LoadValidationErrorsFormData,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { refoldComponents } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import { PendingStates } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateHeader';
import type { InqTmplVersionWithAssetFile } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTheme';
import {
  firstInitialStep,
  uiStepAllComponentConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import {
  componentWithUpdatedTransition,
  stepBackTransitionUpdated,
  stepTransitionUpdated,
  stepWithClearedTransitions,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps-update';
import {
  isRunVerificationActionStep,
  isUpdateInquiryNextStepActionStep,
} from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import {
  InquiryTemplateVersionCombinedStep,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepIntegration,
  InquiryTemplateVersionStepVerifyWithPersona,
  isCombinedStepConfig,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import {
  WorkflowActionUpdateInquiryNextStepNameV1,
  WorkflowRunVerificationAction,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepType,
  WorkflowVersion,
} from 'dashboard/models/workflow-version';
import { createMockImageRemoteStyles } from 'inquiry/lib/stubs/sst/ImageRemote';
import { StyleValueFontWeight, StyleValueJustify } from 'lib/inquiryStyles';
import { StepPreview, StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import {
  ButtonCompleteComponentConfig,
  ImageRemoteComponent,
  isUiTransitionComponentConfig,
  TitleComponent,
  UiComponentConfig,
  UiComponentConfigType,
  UiComponentType,
  UiTransitionComponentConfig,
} from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';
import { getRootDomain } from 'lib/utils/url';
import InquiryParams from 'verify/lib/interfaces/InquiryParams';

// Return titles in least to most frequent order in case of accidental overlap
export function getLoadingModalTitle(pendingStates: PendingStates): string | undefined {
  const { isDiscarding, isLoading, isPublishing, isUpdating } = pendingStates;
  if (isPublishing) {
    return 'Publishing...';
  }
  if (isDiscarding) {
    return 'Discarding...';
  }
  if (isUpdating) {
    return 'Saving...';
  }
  if (isLoading) {
    return 'Loading...';
  }
}

export function stepsWithSanitizedBackSteps(
  steps: InquiryTemplateVersionStep[],
): InquiryTemplateVersionStep[] {
  const stepNames = steps.map(({ name }) => name);
  return steps.map((step) => {
    switch (step.type) {
      case UiStepType.Complete:
      case UiStepType.Loop:
        return step;
      case UiStepType.Document:
      case UiStepType.Integration:
      case UiStepType.VerifyWithPersona:
      case UiStepType.GovernmentId:
      case UiStepType.Selfie:
      case UiStepType.SelfieReflection:
      case UiStepType.Ui: {
        const backStepName = step.config['back-step'];
        if (backStepName != null && !stepNames.includes(backStepName)) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          return stepBackTransitionUpdated({ step, backStepTarget: null });
        } else {
          return step;
        }
      }
      default:
        assertNever(step);
    }
  });
}

/**
 * used when new workflow version and verification template version drafts are created
 * to update the references within the inquiry template version
 */
export type ReferencedIdTuple = { originalId: string; updatedId: string };

export function updatedWorkflowVersionVtmplvReferences(
  workflowVersions: Readonly<WorkflowVersion[]>,
  idsToUpdate: ReferencedIdTuple[],
): WorkflowVersion[] {
  return workflowVersions.map((workflowVersion) => {
    return set(
      ['attributes', 'steps-config'],
      updatedWorkflowVersionSteps(workflowVersion.attributes['steps-config'], idsToUpdate),
      workflowVersion,
    );
  });
}

/**
 * finds and updates previous vtmpl-version-id references in workflow actions
 *
 * @param workflowVersions workflow versions from inquiry template editor form data schema
 * @param idsToUpdate array of old/new vtmpl-version-id tuples
 * @returns workflow versions with updated references
 */
export function updatedWorkflowVersionSteps(
  workflowSteps: Readonly<WorkflowStep[]>,
  idsToUpdate: ReferencedIdTuple[],
): WorkflowStep[] {
  return workflowSteps.map((workflowStep) => {
    switch (workflowStep.type) {
      case WorkflowStepType.Action:
        return isRunVerificationActionStep(workflowStep)
          ? set(
              ['action'],
              updatedWorkflowActionReference(workflowStep.action, idsToUpdate),
              workflowStep,
            )
          : workflowStep;
      case WorkflowStepType.Parallel:
      case WorkflowStepType.Conditional:
        return set(
          ['branches'],
          workflowStep.branches.map((branch) => updatedWorkflowVersionSteps(branch, idsToUpdate)),
          workflowStep,
        );
      default:
        // no vtmpl version ids to update, return step;
        return workflowStep;
    }
  });
}

/**
 * finds and updates previous vtmpl-version-id references in steps config
 *
 * @param steps steps config from inquiry template editor form data
 * @param idsToUpdate array of old/new vtmpl-version-id tuples
 * @returns steps config with updated references
 */
export function updatedStepVtmplvReferences(
  steps: Readonly<InquiryTemplateVersionStep[]>,
  idsToUpdate: ReferencedIdTuple[],
): InquiryTemplateVersionStep[] {
  return steps.map((step) => {
    switch (step.type) {
      case UiStepType.Document:
      case UiStepType.GovernmentId:
      case UiStepType.Selfie:
      case UiStepType.SelfieReflection:
        return updatedStepVtmplvReference(step, idsToUpdate);
      case UiStepType.Complete:
      case UiStepType.Integration:
      case UiStepType.VerifyWithPersona:
      case UiStepType.Ui:
        // no vtmplVersion references in ui steps (for now), return step
        return step;
      case UiStepType.Loop: {
        const updatedLoopSteps = step.config.steps.map((loopStep) => {
          if (isCombinedStepConfig(loopStep)) {
            return updatedStepVtmplvReference(loopStep, idsToUpdate);
          } else {
            return loopStep;
          }
        });
        return {
          ...step,
          config: {
            ...step.config,
            steps: updatedLoopSteps,
          },
        };
      }
      default:
        assertNever(step);
    }
  });
}

/**
 * finds and updates previous workflow-version-id references in steps config
 *
 * @param steps steps config from inquiry template editor form data
 * @param idsToUpdate array of old/new workflow-version-id tuples
 * @returns steps config with updated references
 */
export function updatedWorkflowVersionReferences(
  steps: Readonly<InquiryTemplateVersionStep[]>,
  idsToUpdate: ReferencedIdTuple[],
): InquiryTemplateVersionStep[] {
  return steps.map((step) => {
    switch (step.type) {
      case UiStepType.Complete:
        return step;
      case UiStepType.Document:
      case UiStepType.Integration:
      case UiStepType.VerifyWithPersona:
      case UiStepType.GovernmentId:
      case UiStepType.Selfie:
      case UiStepType.SelfieReflection:
        return updatedStepWorkflowReference(step, idsToUpdate);
      case UiStepType.Ui: {
        const updatedComponents = uiStepAllComponentConfigs(step).map((component) =>
          isUiTransitionComponentConfig(component)
            ? updatedComponentReference(component, idsToUpdate)
            : component,
        );
        return set(['config', 'components'], refoldComponents(updatedComponents), step);
      }
      case UiStepType.Loop: {
        const updatedLoopSteps = step.config.steps.map((loopStep) => {
          if (isCombinedStepConfig(loopStep)) {
            return updatedStepWorkflowReference(loopStep, idsToUpdate);
          } else if (isUiStepConfig(loopStep)) {
            const updatedComponents = uiStepAllComponentConfigs(loopStep).map((component) =>
              isUiTransitionComponentConfig(component)
                ? updatedComponentReference(component, idsToUpdate)
                : component,
            );
            return set(['config', 'components'], refoldComponents(updatedComponents), loopStep);
          } else {
            return loopStep;
          }
        });
        return {
          ...step,
          config: {
            ...step.config,
            steps: updatedLoopSteps,
          },
        };
      }
      default:
        assertNever(step);
    }
  });
}

function updatedStepWorkflowReference(
  step: Readonly<
    | InquiryTemplateVersionCombinedStep
    | InquiryTemplateVersionStepIntegration
    | InquiryTemplateVersionStepVerifyWithPersona
  >,
  idsToUpdate: ReferencedIdTuple[],
):
  | InquiryTemplateVersionCombinedStep
  | InquiryTemplateVersionStepIntegration
  | InquiryTemplateVersionStepVerifyWithPersona {
  const idx = idsToUpdate.findIndex((ids) => ids.originalId === step.config['workflow-version-id']);
  return idx !== -1
    ? set(['config', 'workflow-version-id'], idsToUpdate[idx].updatedId, step)
    : step;
}

function updatedStepVtmplvReference(
  step: Readonly<InquiryTemplateVersionCombinedStep>,
  idsToUpdate: ReferencedIdTuple[],
): InquiryTemplateVersionCombinedStep | InquiryTemplateVersionStepIntegration {
  const idx = idsToUpdate.findIndex(
    (ids) => ids.originalId === step.config['verification-template-version-id'],
  );
  return idx !== -1
    ? set(['config', 'verification-template-version-id'], idsToUpdate[idx].updatedId, step)
    : step;
}

function updatedComponentReference(
  component: Readonly<UiTransitionComponentConfig>,
  idsToUpdate: ReferencedIdTuple[],
): UiTransitionComponentConfig {
  const idx = idsToUpdate.findIndex(
    (ids) => ids.originalId === component.config['workflow-version-id'],
  );
  return idx !== -1
    ? set(['config', 'workflow-version-id'], idsToUpdate[idx].updatedId, component)
    : component;
}

function updatedWorkflowActionReference(
  workflowAction: Readonly<WorkflowRunVerificationAction>,
  idsToUpdate: ReferencedIdTuple[],
): WorkflowRunVerificationAction {
  const idx = idsToUpdate.findIndex(
    (ids) => ids.originalId === workflowAction.params['verification-template-version-id'],
  );
  if (idx !== -1) {
    return set(
      ['params', 'verification-template-version-id'],
      idsToUpdate[idx].updatedId,
      workflowAction,
    );
  } else {
    return workflowAction;
  }
}

/**
 * The first component on the start step which is either a title or hosted image
 */
export function headerComponentIndex(components: UiComponentConfig[]): number {
  return components.findIndex(
    (component) =>
      component.type === UiComponentConfigType.Title ||
      component.type === UiComponentConfigType.ImageHosted,
  );
}

/**
 * updates steps client data for start/logo step prior to inquiry template version draft being updated.
 */
export function mapStepsConfigToStepsPreview(
  /**
   * User-edited form data.
   */
  inquiryTemplateVersionLiveEdited: Readonly<InqTmplVersionWithAssetFile['attributes']>,
  /**
   * Step preview from server. Expected never to change.
   */
  stepsPreview: Readonly<StepPreview[]>,
  hasUnsavedChanges: boolean,
): StepPreview[] {
  const steps = inquiryTemplateVersionLiveEdited.steps;

  if (!hasUnsavedChanges || steps == null || stepsPreview.length === 0) {
    return [...stepsPreview];
  }

  const initialStep = firstInitialStep(steps);
  // return if initial step is null or non-UI step (no components)
  if (!isUiStepConfig(initialStep)) {
    return [...stepsPreview];
  }

  const componentIndex = headerComponentIndex(initialStep.config.components);
  if (componentIndex === -1) {
    return [...stepsPreview];
  }

  const startIndex = steps.findIndex((step) => step.name === initialStep.name);
  let startStepPreview = stepsPreview[startIndex] as StepUi;
  const headerComponent = initialStep.config.components[componentIndex];
  const headerComponentPreview = startStepPreview.config.components[componentIndex];

  // toggle step client data type based on config type (header logo should only ever be image_hosted or title)
  if (headerComponent.type === UiComponentConfigType.ImageHosted) {
    // set url to asset-file URL if one present. otherwise, set to client data step url (if already image_remote). otherwise, set null
    let url = inquiryTemplateVersionLiveEdited['asset-file']?.preview;
    if (headerComponentPreview.type === UiComponentType.ImageRemote && url == null) {
      url = headerComponentPreview.attributes.url;
    }

    const imagePreviewLive: ImageRemoteComponent = {
      name: headerComponent.name,
      type: UiComponentType.ImageRemote,
      attributes: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        url: url,
        width: headerComponent.config.width,
      },
      styles: createMockImageRemoteStyles({
        margin: {
          base: {
            base: {
              top: null,
              bottom: null,
              left: null,
              right: null,
            },
          },
        },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        height: { base: { base: null } },
        width: {
          base: {
            base: headerComponent?.['style-variants']?.[0]?.width,
          },
        },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        justify: { base: { base: null } },
      }),
    };

    // Do not mutate original stepsPreview
    startStepPreview = set(
      ['config', 'components', componentIndex],
      imagePreviewLive,
      startStepPreview,
    );
  } else if (headerComponent.type === UiComponentConfigType.Title) {
    const titlePreviewLive: TitleComponent = {
      name: headerComponent.name,
      type: UiComponentType.Title,
      attributes: {
        text: 'Getting started',
      },
      // Values taken from server-side defaults
      // @see app/models/inquiry/ui_component/title.rb
      styles: {
        margin: {
          base: { base: { top: null, left: null, right: null, bottom: { unit: 'px', value: 16 } } },
        },
        justify: { base: { base: StyleValueJustify.Start } },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'font-family': { base: { base: null } },
        'font-size': { base: { base: { unit: 'px', value: 22 } } },
        'font-weight': { base: { base: StyleValueFontWeight.Medium } },
        'letter-spacing': { base: { base: { unit: 'px', value: 0 } } },
        'line-height': { base: { base: { unit: 'px', value: 32 } } },
        'text-color': { base: { base: { unit: 'hex', value: Color.CoreSlate1000 } } },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'text-color-highlight': { base: { base: null } },
      },
    };

    // Do not mutate original stepsPreview
    startStepPreview = set(
      ['config', 'components', componentIndex],
      titlePreviewLive,
      startStepPreview,
    );
  }

  // Use step with updated preview
  return stepsPreview.map((step, idx) => (idx === startIndex ? startStepPreview : step));
}

/**
 * @returns array of steps with all workflow transitions that just transition the inquiry replaced with a to-step config
 */
export const simplifyStepConfigTransitions = (
  steps: InquiryTemplateVersionStep[],
  workflowVersions: WorkflowVersionMap,
) => {
  return steps.map((step) => {
    if (isUiStepConfig(step)) {
      const updatedComponents = uiStepAllComponentConfigs(step).map((component) => {
        if (isUiTransitionComponentConfig(component) && component?.config['workflow-version-id']) {
          const workflowVersion = workflowVersions[component.config['workflow-version-id']];
          const equivalentStep = workflowVersion ? getEquivalentToStep(workflowVersion) : null;
          if (equivalentStep) {
            return componentWithUpdatedTransition({
              transitionComponent: component,
              transitionType: 'step',
              targetToken: equivalentStep,
            });
          } else if (workflowVersion.attributes['steps-config'].length === 0) {
            // convert back to a button complete because we have a null transition
            return {
              ...component,
              config: omit(
                ['workflow-version-id', 'workflow-on-error-step-name', 'to-step'],
                component.config,
              ),
              type: UiComponentConfigType.ButtonComplete,
            } as ButtonCompleteComponentConfig;
          } else {
            return component;
          }
        } else {
          return component;
        }
      });
      return set(['config', 'components'], refoldComponents(updatedComponents), step);
    } else if (isCombinedStepConfig(step) && step.config['workflow-version-id']) {
      const workflowVersion = workflowVersions[step.config['workflow-version-id']];
      const equivalentStep = getEquivalentToStep(workflowVersion);
      if (equivalentStep) {
        return stepTransitionUpdated({ step, transitionType: 'step', targetToken: equivalentStep });
      } else if (workflowVersion.attributes['steps-config'].length === 0) {
        // remove reference to workflow version if workflow version is empty, so we don't attempt to save an empty workflow version
        // note this will still fail inquiry template step validation
        return stepWithClearedTransitions(step);
      }
    }

    return step;
  });
};

/**
 * @returns true if workflow version just sets inquiry next step
 * and can be replaced with to_step on the step config
 */
export const isTrivialWorkflowVersion = (workflowVersion: WorkflowVersion) => {
  return (
    workflowVersion.attributes['steps-config'].length === 1 &&
    isUpdateInquiryNextStepActionStep(workflowVersion.attributes['steps-config'][0])
  );
};

/**
 * @returns step name workflow routes to if workflow version's only step is update inquiry next step and can be replace with to_step on the step config
 */
export const getEquivalentToStep = (workflowVersion: WorkflowVersion): string | undefined => {
  if (isTrivialWorkflowVersion(workflowVersion)) {
    const workflowStep = workflowVersion.attributes['steps-config'][0] as WorkflowStepAction;
    const action = workflowStep?.action as WorkflowActionUpdateInquiryNextStepNameV1;
    return action?.params?.['next-step-name'];
  }
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return null;
};

/**
 * @returns updated workflow editor path after inquiry template has been saved and the wfv tokens have been updated
 */
export const getUpdatedWorkflowEditorPath = (
  pathname: string,
  workflowVersions: WorkflowVersionMap,
  updatedWorkflowIds: ReferencedIdTuple[],
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): string => {
  const workflowEditorPathMatch = matchPath(
    {
      path: WORKFLOW_EDITOR_BASE_PATH,
      end: false,
    },
    pathname,
  );

  const workflowEditorStepTokenPathMatch = matchPath(
    {
      path: WORKFLOW_EDITOR_STEP_TOKEN_PATH,
      end: false,
    },
    pathname,
  );

  if (workflowEditorPathMatch) {
    if (workflowEditorPathMatch.params.workflowVersionId) {
      const updatedId = updatedWorkflowIds.find(
        ({ originalId }) => originalId === workflowEditorPathMatch.params.workflowVersionId,
      )?.updatedId;

      // if currently viewed workflow version is transition only, after save it will no longer be linked, redirect back to flow view
      if (getEquivalentToStep(workflowVersions[workflowEditorPathMatch.params.workflowVersionId])) {
        return 'flow';
      }

      if (
        workflowEditorStepTokenPathMatch &&
        workflowEditorStepTokenPathMatch.params.workflowStepToken
      ) {
        return generatePath('actions/:workflowVersionId/:workflowStepToken', {
          workflowStepToken: workflowEditorStepTokenPathMatch.params.workflowStepToken,
          workflowVersionId: updatedId || workflowEditorPathMatch.params.workflowVersionId,
        });
      }

      return generatePath('actions/:workflowVersionId', {
        workflowVersionId: updatedId || workflowEditorPathMatch.params.workflowVersionId,
      });
    }
  }
};

/**
 * @returns updated config editor path after inquiry template has been saved and the vtv tokens have been updated
 */
export const getUpdatedVtmplVersionPath = (
  pathname: string,
  updatedVtmplIds: ReferencedIdTuple[],
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): string => {
  const vtmplVersionPathMatch = matchPath(
    {
      path: CONFIG_EDITOR_VERIFICATION_PATH,
    },
    pathname,
  );
  if (vtmplVersionPathMatch && vtmplVersionPathMatch.params.verificationTemplateVersionId) {
    const updatedId = updatedVtmplIds.find(
      ({ originalId }) => originalId === vtmplVersionPathMatch.params.verificationTemplateVersionId,
    )?.updatedId;
    return generatePath('config/verifications/:verificationTemplateVersionId', {
      verificationTemplateVersionId:
        updatedId || vtmplVersionPathMatch.params.verificationTemplateVersionId,
    });
  }
};

const wfvTokenUpdatePaths: ITEFormDataKey[] = ['workflowVersions', 'steps'];
const vtmplvTokenUpdatePaths: ITEFormDataKey[] = [
  'verificationTemplateVersions',
  'workflowVersions',
  'steps',
];
/**
 * Omit pristine ITE form paths to minimize the update request payload.
 * Vtmplvs and workflow versions are unique cases, because other ITE objects
 * hold token references to them within their configs. this means:
 *
 * - if vtmplv/wfv path is dirty, *with* token updates, we must include
 *   wfv + steps in submitted paths as well (both for vtmplv, only steps for wfv),
 *   as we manually update the token references in updateInquiryTemplate()
 * - otherwise we can simply include just the path itself, as no other updates are made.
 */
export const getFormPathsForSubmission = (
  formFieldsDirtyMap: FormFieldDirtyMap,
  vtmplvTokensUpdated: boolean,
  wfvTokensUpdated: boolean,
): ITEFormDataKey[] => {
  let tokenUpdatePaths: ITEFormDataKey[] = [];
  if (vtmplvTokensUpdated) {
    tokenUpdatePaths = vtmplvTokenUpdatePaths;
  } else if (wfvTokensUpdated) {
    tokenUpdatePaths = wfvTokenUpdatePaths;
  }
  const dirtyPaths = (Object.keys(formFieldsDirtyMap) as ITEFormDataKey[]).filter(
    (key: ITEFormDataKey) => formFieldsDirtyMap[key],
  );
  return uniq([...dirtyPaths, ...tokenUpdatePaths]);
};

export const previewInquiryFlow = (
  params: InquiryParams,
  location: Location,
  subdomain: string,
) => {
  if (params['template-id'] == null && params['inquiry-template-id'] == null) {
    captureMessage(
      'Attempted to preview Inquiry flow but both template-id and inquiry-template-id were null',
      'warning',
    );
    return;
  }

  window.open(
    `${location.protocol}//${[subdomain, getRootDomain(location.host)]
      .filter(Boolean)
      .join('.')}/verify?${qs.stringify(params)}`,
    '_blank',
    'noreferrer,noopener',
  );
};

export const openInquiryTemplateVersionPreview = ({
  inquiryTemplateId,
  inquiryTemplateVersionId,
  environmentName,
}: {
  inquiryTemplateId: string;
  inquiryTemplateVersionId: string;
  environmentName?: string;
}) => {
  if (environmentName) {
    window.open(
      `/dashboard/inquiry-templates/${inquiryTemplateId}/${inquiryTemplateVersionId}/preview`,
      '_blank',
    );
  } else {
    window.open(
      `/dashboard/inquiry-templates/${inquiryTemplateId}/${inquiryTemplateVersionId}/preview`,
      '_blank',
    );
  }
};

export const flattenWorkflowStepBranches = (branches: WorkflowStep[][]): WorkflowStep[] => {
  return branches.flatMap((branch) => flattenWorkflowStepBranch(branch));
};

// FIXME: this is largely borrowed from lib/workflows.ts but with some changes -- opportunity to consolidate
const flattenWorkflowStepBranch = (branch: WorkflowStep[]): WorkflowStep[] => {
  return branch.flatMap((node) => {
    switch (node.type) {
      case WorkflowStepType.Conditional:
        return [node, ...flattenWorkflowStepBranches(node.branches)];
      case WorkflowStepType.Parallel:
        return [node, ...flattenWorkflowStepBranches(node.branches)];
      case WorkflowStepType.Loop:
        return [node, ...flattenWorkflowStepBranch(node.branch)];
      default:
        return node;
    }
  });
};

// Determines if the current state of the template (includes VTV and WFV updates) is ready to be validated on the server
export const isReadyForValidation = (formData: LoadValidationErrorsFormData): boolean => {
  // Check WFV updates for any placeholder steps (user has not selected what type of workflow step it is)
  const hasWfvPlaceholderSteps = formData.workflowVersions?.some((wfv) => {
    return flattenWorkflowStepBranch(wfv.attributes['steps-config']).some(
      (step) => step.type === WorkflowStepType.Placeholder,
    );
  });
  return !hasWfvPlaceholderSteps;
};
